import React, { useState } from "react";
import LabelChip from "./LabelChip";
import labelIcon from "../assets/icon_label.svg";
import searchIcon from "../assets/icon_search.svg";

// Shared label strip, mirroring Android's LabelFilterStrip. A circular label
// button on the left; tapping it expands a single horizontal row of chips.
// Modes via props:
//   home — showAllChip + onToggle filters the list (AND semantics like Android)
//   task — onToggle assigns/unassigns the label; onEditLabel adds a per-chip ✎
// onAddLabel renders an "Add Label" button on the right that opens the dialog.
const LabelFilterStrip = ({
  labels,
  selectedIds,
  onToggle,
  onClearAll,
  showAllChip = true,
  onAddLabel,
  onEditLabel,
  showSearchButton = false,
  isSearchActive = false,
  searchQuery = "",
  onSearchToggle = () => {},
  onSearchQueryChange = () => {},
  trailing = null,
}) => {
  const [expanded, setExpanded] = useState(false);
  const hasFilter = selectedIds.size > 0;
  const active = hasFilter || expanded;
  const selectedLabels = labels.filter((l) => selectedIds.has(Number(l.id)));

  return (
    <div style={styles.container}>
      <button
        onClick={() => setExpanded((e) => !e)}
        aria-label="Labels"
        title="Labels"
        style={{
          ...styles.circleBtn,
          borderColor: active ? "var(--primary)" : "var(--border)",
        }}
      >
        {expanded ? (
          <span style={{ ...styles.x, color: "var(--primary)" }}>✕</span>
        ) : (
          <img src={labelIcon} alt="" style={styles.icon} />
        )}
      </button>

      {/* Search button — sits beside the label button; hidden while the chip
          strip is expanded (so they don't fight for the row), mirroring Android. */}
      {showSearchButton && !expanded && (
        <button
          onClick={() => onSearchToggle(!isSearchActive)}
          aria-label="Search tasks"
          title="Search tasks"
          style={{
            ...styles.circleBtn,
            borderColor: isSearchActive ? "var(--primary)" : "var(--border)",
          }}
        >
          {isSearchActive ? (
            <span style={{ ...styles.x, color: "var(--primary)" }}>✕</span>
          ) : (
            <img src={searchIcon} alt="" style={styles.icon} />
          )}
        </button>
      )}

      {isSearchActive ? (
        <input
          autoFocus
          value={searchQuery}
          onChange={(e) => onSearchQueryChange(e.target.value)}
          placeholder="Search tasks…"
          style={styles.searchInput}
        />
      ) : expanded ? (
        <>
          <div style={styles.chipRow}>
            {showAllChip && (
              <span
                onClick={onClearAll}
                style={{
                  ...styles.allChip,
                  ...(hasFilter ? {} : styles.allChipSelected),
                }}
              >
                All
              </span>
            )}
            {labels.map((label) => (
              <span key={label.id} style={styles.chipWrap}>
                <LabelChip
                  name={label.name}
                  color={label.color}
                  selected={selectedIds.has(Number(label.id))}
                  onClick={() => onToggle(Number(label.id))}
                />
              </span>
            ))}
          </div>
          {/* Editing/creating moved into a single management dialog (Android
              parity) — no more per-chip pencils. */}
          {onAddLabel && (
            <button style={styles.addBtn} onClick={onAddLabel}>
              Edit Labels
            </button>
          )}
        </>
      ) : (
        // Collapsed preview: show the selected labels to the right of the
        // button so the assignment is visible at a glance. Tapping a chip
        // expands the strip to edit the selection.
        hasFilter && (
          <div style={styles.chipRow}>
            {selectedLabels.map((label) => (
              <span key={label.id} style={styles.chipWrap}>
                <LabelChip
                  name={label.name}
                  color={label.color}
                  selected
                  onClick={() => setExpanded(true)}
                />
              </span>
            ))}
          </div>
        )
      )}

      {/* Right-edge trailing slot (e.g. the sort button), pushed right. */}
      {trailing && <div style={styles.trailing}>{trailing}</div>}
    </div>
  );
};

const styles = {
  container: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "12px",
    minWidth: 0,
  },
  circleBtn: {
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    border: "2px solid var(--border)",
    backgroundColor: "transparent",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    padding: 0,
  },
  icon: { width: "20px", height: "20px", filter: "var(--icon-filter)" },
  x: { fontSize: "16px", fontWeight: "bold", lineHeight: 1 },
  searchInput: {
    flex: 1,
    minWidth: 0,
    height: "36px",
    padding: "0 12px",
    fontSize: "15px",
    border: "1px solid var(--border)",
    backgroundColor: "var(--surface)",
    color: "var(--text)",
    borderRadius: "18px",
    outline: "none",
  },
  chipRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    overflowX: "auto",
    minWidth: 0,
  },
  allChip: {
    display: "inline-block",
    padding: "4px 12px",
    borderRadius: "999px",
    border: "1px solid var(--text-faint)",
    color: "var(--text-faint)",
    backgroundColor: "transparent",
    fontSize: "12px",
    fontWeight: 600,
    whiteSpace: "nowrap",
    cursor: "pointer",
  },
  allChipSelected: {
    backgroundColor: "var(--text-faint)",
    color: "var(--bg)",
  },
  trailing: { marginLeft: "auto", flexShrink: 0, display: "flex", alignItems: "center" },
  chipWrap: { display: "inline-flex", alignItems: "center", gap: "2px", flexShrink: 0 },
  pencil: {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "var(--text-faint)",
    fontSize: "12px",
    padding: "0 2px",
  },
  addBtn: {
    flexShrink: 0,
    marginLeft: "8px",
    background: "none",
    border: "1px solid var(--primary)",
    color: "var(--primary)",
    borderRadius: "999px",
    padding: "4px 12px",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
};

export default LabelFilterStrip;
